/**
 * Theme resolution.
 *
 * The preference is `system | light | dark`; what gets written to
 * `document.documentElement.dataset.theme` is always a concrete `light` or
 * `dark`, because CSS cannot branch on "system" without duplicating every rule.
 * `index.html` runs the same resolution inline before first paint, so the page
 * never flashes the wrong theme on load.
 */
import { useEffect, useState } from 'react';
import { storageKey } from '../config/branding';
import type { ThemePreference } from '../persistence';

export type ResolvedTheme = 'light' | 'dark';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference;
}

/** Applies the theme to the document and keeps it in step with the OS. */
export function useTheme(preference: ThemePreference): ResolvedTheme {
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(preference));

  useEffect(() => {
    setResolved(resolveTheme(preference));
    if (preference !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const media = window.matchMedia(DARK_QUERY);
    const listen = (): void => setResolved(media.matches ? 'dark' : 'light');
    media.addEventListener('change', listen);
    return () => media.removeEventListener('change', listen);
  }, [preference]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = resolved;
    document.documentElement.style.colorScheme = resolved;
    // Mirrored into localStorage for the pre-paint script in index.html.
    try {
      localStorage.setItem(storageKey('theme'), resolved);
    } catch {
      // Storage can be unavailable in private mode; the theme still applies.
    }
  }, [resolved]);

  return resolved;
}

/** Reflects the reduced-motion preference into an attribute the CSS reads. */
export function useReducedMotion(enabled: boolean): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (enabled) document.documentElement.dataset.reducedMotion = 'true';
    else delete document.documentElement.dataset.reducedMotion;
  }, [enabled]);
}
